import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, DollarSign, Package, ShoppingBag, Users, Printer, ExternalLink, CheckCircle2, AlertCircle, Search, Star, Tag, TrendingUp, Store, Download } from "lucide-react";
import api from "../lib/api";
import { ImageUpload } from "../components/ImageUpload";
import { useSettings } from "../context/SettingsContext";
import { useContent } from "../context/ContentContext";
import { CONTENT_GROUPS } from "../lib/siteContent";

const field = "w-full bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm text-white focus:border-[#FF3B30] outline-none transition-colors";
const CATS = ["shirts", "hoodies", "stickers", "accessories", "monster-shirts"];
const ORDER_STATUSES = ["pending", "paid", "shipped", "delivered", "cancelled"];
const BRANDING_FIELDS = [
  { key: "logo_image", label: "MTL Logo" },
  { key: "hero_image", label: "Homepage Hero" },
  { key: "brand_story_image", label: "Brand Story Photo" },
  { key: "monster_banner_image", label: "Monster Shirts Banner" },
];
const empty = { name: "", description: "", category: "shirts", price: "", images: [], sizes: "", colors: "", color_images: {}, size_prices: {}, printful_product_id: null, stock: 100, featured: false, pod_enabled: false, print_file_url: "", pod_placement: "front", variantsRaw: "", printful_sync: false, fulfiller: "printful", shopify_product_id: "", shopify_variants: {} };
const emptyDiscount = { code: "", type: "percent", value: "", active: true, expires_at: "", max_uses: "", min_subtotal: "" };

const FULFILL_MAP = {
  submitted: { label: "In Production", cls: "text-[#EAB308]" },
  draft: { label: "Draft", cls: "text-zinc-400" },
  pending: { label: "Pending", cls: "text-zinc-400" },
  processing: { label: "Processing", cls: "text-zinc-400" },
  manual: { label: "Manual", cls: "text-zinc-400" },
  shipped: { label: "Shipped", cls: "text-green-500" },
  fulfilled: { label: "Shipped", cls: "text-green-500" },
  error: { label: "Error", cls: "text-[#FF3B30]" },
  failed: { label: "Failed", cls: "text-[#FF3B30]" },
  order_failed: { label: "Failed", cls: "text-[#FF3B30]" },
  order_canceled: { label: "Canceled", cls: "text-[#FF3B30]" },
  unmapped: { label: "Unmapped", cls: "text-[#FF3B30]" },
};
const FulfillmentBadge = ({ provider, status }) => {
  const m = FULFILL_MAP[status] || { label: status, cls: "text-zinc-400" };
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest">
      <span className="text-zinc-500">{provider}:</span><span className={m.cls}>{m.label}</span>
    </span>
  );
};

const variantsToStr = (v) => Object.entries(v || {}).map(([k, val]) => `${k}:${val}`).join(", ");
const strToVariants = (s) => {
  const out = {};
  (s || "").split(",").map((x) => x.trim()).filter(Boolean).forEach((pair) => {
    const [k, val] = pair.split(":").map((y) => y.trim());
    if (k && val && !isNaN(Number(val))) out[k] = Number(val);
  });
  return out;
};

export default function Admin() {
  const [tab, setTab] = useState("products");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [pf, setPf] = useState(null);
  const [branding, setBranding] = useState({});
  const [siteText, setSiteText] = useState({});
  // Printful catalog picker state
  const [pfOpen, setPfOpen] = useState(false);
  const [pfSearch, setPfSearch] = useState("");
  const [pfResults, setPfResults] = useState([]);
  const [pfSelected, setPfSelected] = useState(null);
  const [pfColor, setPfColor] = useState("");
  const [pfBusy, setPfBusy] = useState(false);
  const [imgUrl, setImgUrl] = useState("");
  const [pfMode, setPfMode] = useState("store");
  const [pfStore, setPfStore] = useState([]);
  const { refresh: refreshSettings } = useSettings();
  const { refresh: refreshContent } = useContent();
  const [discounts, setDiscounts] = useState([]);
  const [discountForm, setDiscountForm] = useState(emptyDiscount);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [profit, setProfit] = useState(null);
  const [profitLoading, setProfitLoading] = useState(false);
  const [shopifyStatus, setShopifyStatus] = useState(null);
  const [shopifyProducts, setShopifyProducts] = useState([]);
  const [shopifyLoading, setShopifyLoading] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [importCategory, setImportCategory] = useState("shirts");

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/printful/status").then((r) => setPf(r.data)).catch(() => setPf({ connected: false }));
    api.get("/settings").then((r) => setBranding(r.data)).catch(() => {});
    api.get("/content").then((r) => setSiteText(r.data)).catch(() => {});
    api.get("/admin/discounts").then((r) => setDiscounts(r.data)).catch(() => {});
    api.get("/admin/shopify/status").then((r) => setShopifyStatus(r.data)).catch(() => setShopifyStatus({ connected: false }));
  };
  useEffect(loadAll, []);

  const loadShopifyProducts = async () => {
    setShopifyLoading(true);
    try { const { data } = await api.get("/admin/shopify/products"); setShopifyProducts(data); if (!data.length) toast.info("No products found in Shopify yet"); }
    catch (e) { toast.error(e?.response?.data?.detail ? "Shopify: " + String(e.response.data.detail).slice(0, 80) : "Could not load Shopify products"); }
    finally { setShopifyLoading(false); }
  };
  const importShopifyProduct = async (p) => {
    setImportingId(p.id);
    try { await api.post("/admin/shopify/import", { shopify_product_id: p.id, category: importCategory }); toast.success(`Imported "${p.title}"`); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail ? String(e.response.data.detail).slice(0, 90) : "Import failed"); }
    finally { setImportingId(null); }
  };

  const saveDiscount = async (e) => {
    e.preventDefault();
    const payload = {
      code: discountForm.code.trim().toUpperCase(),
      type: discountForm.type,
      value: parseFloat(discountForm.value) || 0,
      active: !!discountForm.active,
      expires_at: discountForm.expires_at ? new Date(discountForm.expires_at).toISOString() : null,
      max_uses: discountForm.max_uses ? parseInt(discountForm.max_uses) : null,
      min_subtotal: discountForm.min_subtotal ? parseFloat(discountForm.min_subtotal) : null,
    };
    if (!payload.code || !payload.value) { toast.error("Code and value are required"); return; }
    try {
      if (editingDiscount) { await api.put(`/admin/discounts/${editingDiscount}`, payload); toast.success("Discount updated"); }
      else { await api.post("/admin/discounts", payload); toast.success("Discount created"); }
      setDiscountForm(emptyDiscount); setEditingDiscount(null);
      api.get("/admin/discounts").then((r) => setDiscounts(r.data));
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const editDiscount = (d) => {
    setEditingDiscount(d.id);
    setDiscountForm({
      code: d.code, type: d.type, value: String(d.value), active: d.active,
      expires_at: d.expires_at ? d.expires_at.slice(0, 10) : "",
      max_uses: d.max_uses ?? "", min_subtotal: d.min_subtotal ?? "",
    });
  };
  const cancelDiscountEdit = () => { setDiscountForm(emptyDiscount); setEditingDiscount(null); };
  const deleteDiscount = async (id) => {
    if (!window.confirm("Delete this discount code?")) return;
    try { await api.delete(`/admin/discounts/${id}`); toast.success("Deleted"); api.get("/admin/discounts").then((r) => setDiscounts(r.data)); }
    catch { toast.error("Delete failed"); }
  };
  const toggleDiscountActive = async (d) => {
    try {
      await api.put(`/admin/discounts/${d.id}`, {
        code: d.code, type: d.type, value: d.value, active: !d.active,
        expires_at: d.expires_at || null, max_uses: d.max_uses ?? null, min_subtotal: d.min_subtotal ?? null,
      });
      api.get("/admin/discounts").then((r) => setDiscounts(r.data));
    } catch { toast.error("Update failed"); }
  };

  const fetchProfit = async (productId) => {
    setProfitLoading(true); setProfit(null);
    try { const { data } = await api.get(`/admin/products/${productId}/profit`); setProfit(data); }
    catch { toast.error("Could not calculate profit"); }
    finally { setProfitLoading(false); }
  };

  const saveBranding = async () => {
    try {
      await api.put("/admin/settings", branding);
      toast.success("Branding updated");
      refreshSettings();
    } catch { toast.error("Save failed"); }
  };

  const saveContent = async () => {
    try {
      await api.put("/admin/content", siteText);
      toast.success("Content updated");
      refreshContent();
    } catch { toast.error("Save failed"); }
  };

  const searchCatalog = async () => {
    setPfBusy(true); setPfSelected(null);
    try {
      const { data } = await api.get("/admin/printful/catalog", { params: { search: pfSearch } });
      setPfResults(data);
      if (data.length === 0) toast.info("No products found — try another search");
    } catch { toast.error("Catalog search failed"); }
    finally { setPfBusy(false); }
  };

  const selectCatalogProduct = async (id) => {
    setPfBusy(true);
    try {
      const { data } = await api.get(`/admin/printful/catalog/${id}`);
      setPfSelected(data);
      setPfColor(data.colors?.[0] || "");
    } catch { toast.error("Could not load variants"); }
    finally { setPfBusy(false); }
  };

  const applyCatalogVariants = () => {
    if (!pfSelected) return;
    const matches = pfSelected.variants.filter((v) => !pfColor || v.color === pfColor);
    const map = {};
    matches.forEach((v) => { map[v.size || "default"] = v.id; });
    const raw = Object.entries(map).map(([k, val]) => `${k}:${val}`).join(", ");
    const sizeList = matches.map((v) => v.size).filter(Boolean);
    setForm((f) => ({ ...f, variantsRaw: raw, sizes: sizeList.length ? sizeList.join(", ") : f.sizes, printful_sync: false }));
    toast.success(`Filled ${matches.length} variant ID(s)`);
    setPfOpen(false);
  };

  const loadStoreProducts = async () => {
    setPfBusy(true);
    try {
      const { data } = await api.get("/admin/printful/store-products");
      setPfStore(data);
      if (data.length === 0) toast.info("No products found in your Printful store yet");
    } catch { toast.error("Could not load your Printful products"); }
    finally { setPfBusy(false); }
  };

  const importStoreProduct = async (id) => {
    setPfBusy(true);
    try {
      const { data } = await api.get(`/admin/printful/store-products/${id}`);
      const raw = Object.entries(data.printful_variants).map(([k, v]) => `${k}:${v}`).join(", ");
      setForm((f) => ({
        ...f,
        name: f.name || data.name,
        price: f.price || String(data.price || ""),
        sizes: data.sizes.join(", "),
        colors: (data.colors || []).join(", "),
        color_images: data.color_images || {},
        size_prices: data.size_prices || {},
        printful_product_id: data.printful_product_id || null,
        variantsRaw: raw,
        pod_enabled: true,
        printful_sync: true,
        images: [...f.images, ...data.images.filter((im) => !f.images.includes(im))],
      }));
      toast.success(`Imported "${data.name}" — ${data.sizes.length || 1} size(s), ${(data.colors || []).length} color(s)`);
      setPfOpen(false);
    } catch { toast.error("Import failed"); }
    finally { setPfBusy(false); }
  };

  const openPicker = () => {
    const next = !pfOpen;
    setPfOpen(next);
    if (next && pfMode === "store" && pfStore.length === 0) loadStoreProducts();
  };

  const openNew = () => { setEditing(null); setForm(empty); setProfit(null); setModal(true); };
  const openEdit = (p) => {
    setEditing(p);
    setProfit(null);
    setForm({
      ...p, price: String(p.price), images: p.images || [], sizes: (p.sizes || []).join(", "),
      colors: (p.colors || []).join(", "), color_images: p.color_images || {}, size_prices: p.size_prices || {}, printful_product_id: p.printful_product_id || null,
      pod_enabled: !!p.pod_enabled, print_file_url: p.print_file_url || "", pod_placement: p.pod_placement || "front",
      variantsRaw: variantsToStr(p.printful_variants),
    });
    setModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const sizeList = form.sizes.split(",").map((s) => s.trim()).filter(Boolean);
    const sizePrices = {};
    sizeList.forEach((s) => {
      const v = form.size_prices?.[s];
      if (v !== "" && v != null && !isNaN(Number(v))) sizePrices[s] = Number(v);
    });
    const payload = {
      name: form.name, description: form.description, category: form.category,
      price: parseFloat(form.price) || 0,
      images: form.images,
      sizes: sizeList,
      colors: form.colors.split(",").map((s) => s.trim()).filter(Boolean),
      color_images: form.color_images || {},
      size_prices: sizePrices,
      printful_product_id: form.printful_product_id || null,
      stock: parseInt(form.stock) || 0, featured: !!form.featured,
      pod_enabled: !!form.pod_enabled, print_file_url: form.print_file_url || "",
      pod_placement: form.pod_placement || "front", printful_variants: strToVariants(form.variantsRaw),
      printful_sync: !!form.printful_sync,
      fulfiller: form.fulfiller || "printful",
      shopify_product_id: form.shopify_product_id || "",
      shopify_variants: form.shopify_variants || {},
    };
    try {
      if (editing) { await api.put(`/products/${editing.id}`, payload); toast.success("Product updated"); }
      else { await api.post("/products", payload); toast.success("Product created"); }
      setModal(false); loadAll();
    } catch { toast.error("Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Deleted"); loadAll(); } catch { toast.error("Delete failed"); }
  };

  const setOrderStatus = async (id, status) => {
    try { await api.put(`/admin/orders/${id}`, { status }); toast.success("Order updated"); loadAll(); } catch { toast.error("Update failed"); }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("Delete this order permanently?")) return;
    try { await api.delete(`/admin/orders/${id}`); toast.success("Order deleted"); loadAll(); } catch { toast.error("Delete failed"); }
  };

  const [purging, setPurging] = useState(false);
  const purgeTestOrders = async () => {
    if (!window.confirm("Delete all unpaid/abandoned (test) orders? Paid orders are always kept.")) return;
    setPurging(true);
    try { const { data } = await api.post("/admin/orders/purge-unpaid"); toast.success(`Removed ${data.deleted} test order(s)`); loadAll(); }
    catch { toast.error("Cleanup failed"); }
    finally { setPurging(false); }
  };

  const registerWebhook = async () => {
    try { const { data } = await api.post("/admin/printful/register-webhook"); toast.success("Webhook registered"); } catch { toast.error("Webhook registration failed"); }
  };
  const registerShopifyWebhook = async () => {
    try { await api.post("/admin/shopify/register-webhook"); toast.success("NinjaPOD tracking webhook registered"); }
    catch (e) { toast.error(e?.response?.data?.detail ? String(e.response.data.detail).slice(0, 90) : "Registration failed"); }
  };
  const [syncingPrices, setSyncingPrices] = useState(false);
  const syncShopifyPrices = async () => {
    setSyncingPrices(true);
    try {
      const { data } = await api.post("/admin/shopify/sync-prices");
      if (data.count === 0) toast.success(`Prices already match Shopify (${data.checked} checked)`);
      else toast.success(`Updated ${data.count} price${data.count > 1 ? "s" : ""}: ${data.updated.map((u) => `${u.name} $${u.old}→$${u.new}`).join(", ")}`);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail ? String(e.response.data.detail).slice(0, 90) : "Price sync failed"); }
    finally { setSyncingPrices(false); }
  };

  const [resyncing, setResyncing] = useState(false);
  const resyncColors = async () => {
    if (!window.confirm("Re-sync all Printful products to pull in colors, sizes & mockups? This updates existing listings.")) return;
    setResyncing(true);
    try {
      const { data } = await api.post("/admin/printful/resync-all");
      toast.success(`Synced ${data.updated.length} product(s)${data.skipped.length ? ` · ${data.skipped.length} skipped` : ""}`);
      loadAll();
    } catch { toast.error("Sync failed"); }
    finally { setResyncing(false); }
  };

  const statCards = stats ? [
    { icon: DollarSign, label: "Revenue", value: `$${stats.revenue.toFixed(2)}` },
    { icon: ShoppingBag, label: "Orders", value: stats.total_orders },
    { icon: Package, label: "Products", value: stats.total_products },
    { icon: Users, label: "Customers", value: stats.total_users },
  ] : [];

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10" data-testid="admin-page">
      <h1 className="font-display text-4xl uppercase tracking-tighter mb-6">Admin <span className="text-[#FF3B30]">Dashboard</span></h1>

      {/* PRINTFUL FULFILLMENT STATUS */}
      <div className="border border-zinc-800 bg-zinc-900 p-4 mb-8 flex flex-wrap items-center justify-between gap-3" data-testid="printful-status">
        <div className="flex items-center gap-3">
          <Printer className={pf?.connected ? "text-[#EAB308]" : "text-zinc-600"} size={20} />
          <div>
            <div className="text-sm font-bold uppercase tracking-widest">Printful Fulfillment</div>
            <div className="text-xs text-zinc-500 flex items-center gap-2 mt-0.5">
              {pf?.connected
                ? <><CheckCircle2 size={12} className="text-green-500" /> Connected · orders {pf.confirm_orders ? "auto-confirmed (LIVE)" : "created as DRAFT (test mode)"}</>
                : <><AlertCircle size={12} className="text-[#FF3B30]" /> Not connected</>}
            </div>
          </div>
        </div>
        <button onClick={registerWebhook} data-testid="register-webhook-btn" className="text-xs font-bold uppercase tracking-widest border border-zinc-700 px-4 py-2 hover:border-white transition-colors">Register Tracking Webhook</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((s, i) => (
          <div key={i} className="border border-zinc-800 bg-zinc-900 p-5" data-testid={`stat-${s.label.toLowerCase()}`}>
            <s.icon className="text-[#FF3B30] mb-2" size={20} />
            <div className="font-display text-3xl">{s.value}</div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-3 mb-6 border-b border-zinc-800">
        {["products", "orders", "discounts", "shopify", "branding", "content"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
            className={`px-5 py-3 text-xs font-bold uppercase tracking-widest transition-colors ${tab === t ? "text-white border-b-2 border-[#FF3B30]" : "text-zinc-500 hover:text-white"}`}>{t}</button>
        ))}
      </div>

      {tab === "products" && (
        <div>
          <div className="flex justify-end gap-3 mb-4">
            {pf?.connected && (
              <button onClick={resyncColors} disabled={resyncing} data-testid="resync-colors-button" className="flex items-center gap-2 border border-[#EAB308] text-[#EAB308] font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-[#EAB308] hover:text-black transition-colors duration-200 disabled:opacity-50"><Printer size={16} /> {resyncing ? "Syncing…" : "Sync Colors from Printful"}</button>
            )}
            <button onClick={openNew} data-testid="add-product-button" className="flex items-center gap-2 bg-[#FF3B30] text-white font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white hover:text-black transition-colors duration-200"><Plus size={16} /> Add Product</button>
          </div>
          <div className="border border-zinc-800 overflow-x-auto">
            <table className="w-full text-sm" data-testid="products-table">
              <thead><tr className="bg-zinc-900 text-left text-xs uppercase tracking-widest text-zinc-500">
                <th className="p-3">Product</th><th className="p-3">Category</th><th className="p-3">Fulfiller</th><th className="p-3">Price</th><th className="p-3">Stock</th><th className="p-3">POD</th><th className="p-3 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-t border-zinc-800" data-testid={`product-row-${p.id}`}>
                    <td className="p-3 flex items-center gap-3">{p.images?.[0] ? <img src={p.images[0]} alt="" className="h-10 w-10 object-cover border border-zinc-800" /> : <div className="h-10 w-10 border border-zinc-800 bg-zinc-950" />}<span className="font-bold">{p.name}</span></td>
                    <td className="p-3 text-zinc-400 uppercase text-xs">{p.category}</td>
                    <td className="p-3" data-testid={`fulfiller-${p.id}`}>
                      {p.fulfiller === "shopify_ninjapod"
                        ? <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#22C55E] border border-green-500/40 px-2 py-1"><Store size={11} /> NinjaPOD</span>
                        : <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-sky-400 border border-sky-500/40 px-2 py-1"><Printer size={11} /> Printful</span>}
                    </td>
                    <td className="p-3 text-[#FF3B30] font-bold">${p.price.toFixed(2)}</td>
                    <td className="p-3 text-zinc-400">{p.stock}</td>
                    <td className="p-3">{p.pod_enabled
                      ? <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#EAB308]"><Printer size={12} /> On</span>
                      : <span className="text-[10px] uppercase tracking-widest text-zinc-600">Off</span>}</td>
                    <td className="p-3 text-right">
                      <button onClick={() => openEdit(p)} data-testid={`edit-${p.id}`} className="text-zinc-400 hover:text-white mr-3"><Pencil size={16} /></button>
                      <button onClick={() => del(p.id)} data-testid={`delete-${p.id}`} className="text-zinc-400 hover:text-[#FF3B30]"><Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-xs text-zinc-500 uppercase tracking-widest">{orders.filter((o) => o.payment_status === "paid").length} paid · {orders.length} total</p>
            <button onClick={purgeTestOrders} disabled={purging} data-testid="clear-test-orders-button" className="flex items-center gap-2 border border-zinc-700 text-zinc-300 font-bold uppercase tracking-widest text-xs px-4 py-2.5 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors duration-200 disabled:opacity-50">
              <Trash2 size={14} /> {purging ? "Clearing…" : "Clear Test / Unpaid Orders"}
            </button>
          </div>
          <div className="border border-zinc-800 overflow-x-auto">
          <table className="w-full text-sm" data-testid="orders-table">
            <thead><tr className="bg-zinc-900 text-left text-xs uppercase tracking-widest text-zinc-500">
              <th className="p-3">Order</th><th className="p-3">Customer</th><th className="p-3">Items</th><th className="p-3">Total</th><th className="p-3">Payment</th><th className="p-3">Fulfillment</th><th className="p-3">Status</th><th className="p-3"></th>
            </tr></thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-zinc-800" data-testid={`order-row-${o.id}`}>
                  <td className="p-3 text-zinc-400">#{o.id.slice(-8)}</td>
                  <td className="p-3"><div className="font-bold">{o.shipping?.name}</div><div className="text-xs text-zinc-500">{o.email}</div></td>
                  <td className="p-3 text-zinc-400">{o.items?.length}</td>
                  <td className="p-3 text-[#FF3B30] font-bold">${o.total?.toFixed(2)}</td>
                  <td className="p-3"><span className={`text-xs uppercase tracking-widest ${o.payment_status === "paid" ? "text-[#EAB308]" : "text-zinc-500"}`}>{o.payment_status}</span></td>
                  <td className="p-3">
                    <div className="flex flex-col gap-1 items-start" data-testid={`fulfillment-${o.id}`}>
                      {o.pod_status && <FulfillmentBadge provider="Printful" status={o.pod_status} />}
                      {o.shopify_status && <FulfillmentBadge provider="NinjaPOD" status={o.shopify_status} />}
                      {!o.pod_status && !o.shopify_status && <span className="text-[10px] uppercase tracking-widest text-zinc-600">—</span>}
                      {o.tracking_url && <a href={o.tracking_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-[10px] text-[#EAB308] hover:underline">Track <ExternalLink size={10} /></a>}
                    </div>
                  </td>
                  <td className="p-3">
                    <select value={o.status} onChange={(e) => setOrderStatus(o.id, e.target.value)} data-testid={`order-status-${o.id}`} className="bg-zinc-950 border border-zinc-700 px-2 py-1.5 text-xs uppercase tracking-widest text-white outline-none focus:border-[#FF3B30]">
                      {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="p-3">
                    <button onClick={() => deleteOrder(o.id)} data-testid={`delete-order-${o.id}`} aria-label="Delete order" className="text-zinc-600 hover:text-[#FF3B30] transition-colors"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan="8" className="p-8 text-center text-zinc-500">No orders yet.</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {tab === "discounts" && (
        <div className="grid lg:grid-cols-3 gap-8" data-testid="discounts-tab">
          <form onSubmit={saveDiscount} className="lg:col-span-1 border border-zinc-800 bg-zinc-900 p-5 space-y-3 h-fit" data-testid="discount-form">
            <h3 className="font-display text-xl uppercase tracking-tight">{editingDiscount ? "Edit" : "New"} Discount</h3>
            <input className={field + " uppercase"} placeholder="Code (e.g. SAVE10)" value={discountForm.code} onChange={(e) => setDiscountForm({ ...discountForm, code: e.target.value })} data-testid="discount-code-input" />
            <div className="grid grid-cols-2 gap-3">
              <select className={field} value={discountForm.type} onChange={(e) => setDiscountForm({ ...discountForm, type: e.target.value })} data-testid="discount-type-select">
                <option value="percent">% Off</option>
                <option value="fixed">$ Off</option>
              </select>
              <input className={field} type="number" step="0.01" min="0" placeholder={discountForm.type === "percent" ? "10 (=10%)" : "5.00"} value={discountForm.value} onChange={(e) => setDiscountForm({ ...discountForm, value: e.target.value })} data-testid="discount-value-input" />
            </div>
            <label className="block text-[11px] uppercase tracking-widest text-zinc-500">Expires (optional)</label>
            <input className={field} type="date" value={discountForm.expires_at} onChange={(e) => setDiscountForm({ ...discountForm, expires_at: e.target.value })} data-testid="discount-expires-input" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-1">Max uses</label>
                <input className={field} type="number" min="1" placeholder="∞" value={discountForm.max_uses} onChange={(e) => setDiscountForm({ ...discountForm, max_uses: e.target.value })} data-testid="discount-maxuses-input" />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-1">Min subtotal $</label>
                <input className={field} type="number" step="0.01" min="0" placeholder="0" value={discountForm.min_subtotal} onChange={(e) => setDiscountForm({ ...discountForm, min_subtotal: e.target.value })} data-testid="discount-minsubtotal-input" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" checked={discountForm.active} onChange={(e) => setDiscountForm({ ...discountForm, active: e.target.checked })} data-testid="discount-active-checkbox" /> Active</label>
            <div className="flex gap-2 pt-1">
              <button type="submit" data-testid="save-discount-button" className="flex-1 bg-[#FF3B30] text-white font-bold uppercase tracking-widest text-xs px-4 py-3 hover:bg-white hover:text-black transition-colors">{editingDiscount ? "Update" : "Create"} Code</button>
              {editingDiscount && <button type="button" onClick={cancelDiscountEdit} className="border border-zinc-700 text-xs font-bold uppercase tracking-widest px-4 hover:border-white transition-colors">Cancel</button>}
            </div>
          </form>

          <div className="lg:col-span-2 border border-zinc-800 overflow-x-auto h-fit">
            <table className="w-full text-sm" data-testid="discounts-table">
              <thead><tr className="bg-zinc-900 text-left text-xs uppercase tracking-widest text-zinc-500">
                <th className="p-3">Code</th><th className="p-3">Value</th><th className="p-3">Uses</th><th className="p-3">Expires</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {discounts.map((d) => (
                  <tr key={d.id} className="border-t border-zinc-800" data-testid={`discount-row-${d.id}`}>
                    <td className="p-3 font-bold flex items-center gap-2"><Tag size={14} className="text-[#EAB308]" /> {d.code}</td>
                    <td className="p-3 text-[#FF3B30] font-bold">{d.type === "percent" ? `${d.value}%` : `$${d.value.toFixed(2)}`}</td>
                    <td className="p-3 text-zinc-400">{d.used_count || 0}{d.max_uses ? ` / ${d.max_uses}` : ""}</td>
                    <td className="p-3 text-zinc-400 text-xs">{d.expires_at ? new Date(d.expires_at).toLocaleDateString() : "—"}</td>
                    <td className="p-3">
                      <button onClick={() => toggleDiscountActive(d)} data-testid={`toggle-discount-${d.id}`} className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 border ${d.active ? "text-green-400 border-green-500/40" : "text-zinc-500 border-zinc-700"}`}>{d.active ? "Active" : "Off"}</button>
                    </td>
                    <td className="p-3 text-right">
                      <button onClick={() => editDiscount(d)} data-testid={`edit-discount-${d.id}`} className="text-zinc-400 hover:text-white mr-3"><Pencil size={16} /></button>
                      <button onClick={() => deleteDiscount(d.id)} data-testid={`delete-discount-${d.id}`} className="text-zinc-400 hover:text-[#FF3B30]"><Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
                {discounts.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-zinc-500">No discount codes yet. Create one on the left.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "shopify" && (
        <div className="space-y-6" data-testid="shopify-tab">
          <div className="border border-zinc-800 bg-zinc-900 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="shopify-status">
            <div className="flex items-center gap-3">
              <Store className={shopifyStatus?.connected ? "text-[#22C55E]" : "text-zinc-600"} size={20} />
              <div>
                <div className="text-sm font-bold uppercase tracking-widest">Shopify → NinjaPOD Bridge</div>
                <div className="text-xs text-zinc-500 flex items-center gap-2 mt-0.5">
                  {shopifyStatus?.connected
                    ? <><CheckCircle2 size={12} className="text-green-500" /> Connected · {shopifyStatus.shop} ({shopifyStatus.domain})</>
                    : <><AlertCircle size={12} className="text-[#FF3B30]" /> Not connected{shopifyStatus?.reason ? ` — ${String(shopifyStatus.reason).slice(0, 70)}` : ""}</>}
                </div>
              </div>
            </div>
            <button onClick={loadShopifyProducts} disabled={shopifyLoading || !shopifyStatus?.connected} data-testid="shopify-load-products" className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest border border-zinc-700 px-4 py-2.5 hover:border-white transition-colors disabled:opacity-40">
              <Search size={14} /> {shopifyLoading ? "Loading…" : "Load Shopify Products"}
            </button>
            {shopifyStatus?.connected && (
              <button onClick={registerShopifyWebhook} data-testid="shopify-register-webhook" className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest border border-zinc-700 px-4 py-2.5 hover:border-white transition-colors">
                Register Tracking Webhook
              </button>
            )}
            {shopifyStatus?.connected && (
              <button onClick={syncShopifyPrices} disabled={syncingPrices} data-testid="shopify-sync-prices" className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest border border-[#22C55E] text-[#22C55E] px-4 py-2.5 hover:bg-[#22C55E] hover:text-black transition-colors disabled:opacity-50">
                <DollarSign size={14} /> {syncingPrices ? "Syncing…" : "Sync Prices from Shopify"}
              </button>
            )}
          </div>

          {!shopifyStatus?.connected && (
            <p className="text-sm text-zinc-400 max-w-2xl">Add your Shopify Admin API access token (starts with <code className="text-zinc-300">shpat_</code>) to connect. Products you create in the NinjaPOD app will appear here to import into your storefront.</p>
          )}

          {shopifyStatus?.connected && (
            <div className="flex items-center gap-3">
              <span className="text-xs uppercase tracking-widest text-zinc-500">Import into category:</span>
              <select value={importCategory} onChange={(e) => setImportCategory(e.target.value)} data-testid="shopify-import-category" className={field + " w-auto"}>
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}

          {shopifyProducts.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="shopify-products-grid">
              {shopifyProducts.map((p) => (
                <div key={p.id} className="border border-zinc-800 bg-zinc-900 p-4 flex flex-col" data-testid={`shopify-product-${p.id.split('/').pop()}`}>
                  <div className="aspect-square bg-zinc-950 border border-zinc-800 overflow-hidden mb-3 flex items-center justify-center">
                    {p.images?.[0] ? <img src={p.images[0]} alt={p.title} className="h-full w-full object-cover" /> : <Package className="text-zinc-700" size={40} />}
                  </div>
                  <h3 className="font-bold text-sm uppercase tracking-tight leading-tight">{p.title}</h3>
                  <p className="text-xs text-zinc-500 mt-1">{p.variants.length} variant{p.variants.length !== 1 ? "s" : ""}{p.variants[0]?.price ? ` · from $${Number(p.variants[0].price).toFixed(2)}` : ""}</p>
                  <button onClick={() => importShopifyProduct(p)} disabled={importingId === p.id} data-testid={`import-shopify-${p.id.split('/').pop()}`} className="mt-3 flex items-center justify-center gap-2 bg-[#EAB308] text-black font-bold uppercase tracking-widest text-xs px-4 py-2.5 hover:bg-white transition-colors disabled:opacity-50">
                    <Download size={14} /> {importingId === p.id ? "Importing…" : "Import to Store"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "branding" && (
        <div className="space-y-6" data-testid="branding-tab">
          <p className="text-sm text-zinc-400 max-w-2xl">Upload your own photos for the site's key imagery. Changes go live instantly across the storefront.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {BRANDING_FIELDS.map((b) => (
              <div key={b.key} className="border border-zinc-800 bg-zinc-900 p-4" data-testid={`branding-${b.key}`}>
                <div className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-3">{b.label}</div>
                <div className="aspect-video bg-zinc-950 border border-zinc-800 overflow-hidden mb-3 flex items-center justify-center">
                  {branding[b.key]
                    ? <img src={branding[b.key]} alt={b.label} className="h-full w-full object-contain" />
                    : <span className="text-zinc-700 text-xs">No image</span>}
                </div>
                <input className={field + " mb-3"} placeholder="Image URL" value={branding[b.key] || ""} onChange={(e) => setBranding({ ...branding, [b.key]: e.target.value })} data-testid={`branding-input-${b.key}`} />
                <ImageUpload testId={`branding-upload-${b.key}`} label="Upload Photo" onUploaded={(url) => setBranding({ ...branding, [b.key]: url })} />
              </div>
            ))}
          </div>
          <button onClick={saveBranding} data-testid="save-branding-button" className="bg-[#FF3B30] text-white font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-white hover:text-black transition-colors duration-200">Save Branding</button>
        </div>
      )}

      {tab === "content" && (
        <div className="space-y-8" data-testid="content-tab">
          <p className="text-sm text-zinc-400 max-w-2xl">Edit the site's text. "Red part" fields are the accent-colored words in split headlines. Changes go live instantly.</p>
          {CONTENT_GROUPS.map((g) => (
            <div key={g.group} className="border border-zinc-800 bg-zinc-900 p-5" data-testid={`content-group-${g.group.toLowerCase().replace(/\s/g, "-")}`}>
              <h3 className="font-display text-xl uppercase tracking-tight mb-4">{g.group}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {g.fields.map((f) => (
                  <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                    <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1.5">{f.label}</label>
                    {f.type === "textarea"
                      ? <textarea rows="3" className={field} value={siteText[f.key] || ""} onChange={(e) => setSiteText({ ...siteText, [f.key]: e.target.value })} data-testid={`content-${f.key}`} />
                      : <input className={field} value={siteText[f.key] || ""} onChange={(e) => setSiteText({ ...siteText, [f.key]: e.target.value })} data-testid={`content-${f.key}`} />}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <button onClick={saveContent} data-testid="save-content-button" className="bg-[#FF3B30] text-white font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-white hover:text-black transition-colors duration-200">Save Content</button>
        </div>
      )}



      {modal && (
        <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4" onClick={() => setModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-zinc-950 border border-zinc-700 w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-3" data-testid="product-modal">
            <div className="flex justify-between items-center mb-2">
              <h2 className="font-display text-2xl uppercase tracking-tight">{editing ? "Edit" : "New"} Product</h2>
              <button type="button" onClick={() => setModal(false)}><X className="text-zinc-400 hover:text-white" /></button>
            </div>
            <input className={field} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" required />
            <textarea className={field} placeholder="Description" rows="3" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="form-description" />
            <div className="grid grid-cols-2 gap-3">
              <select className={field} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="form-category">
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input className={field} type="number" step="0.01" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="form-price" required />
            </div>
            <div className="border border-zinc-800 p-3 space-y-3" data-testid="product-images">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-widest text-zinc-400">Product Photos{form.images.length ? ` (${form.images.length})` : ""}</span>
                <ImageUpload testId="product-image-upload" label="Add Photos" multiple onUploaded={(url) => setForm((f) => ({ ...f, images: [...f.images, url] }))} />
              </div>
              {form.images.length > 0 ? (
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                  {form.images.map((url, i) => (
                    <div key={i} className="relative group aspect-square border border-zinc-800 overflow-hidden" data-testid={`product-image-${i}`}>
                      <img src={url} alt="" className="h-full w-full object-cover" />
                      {i === 0 && <span className="absolute top-1 left-1 bg-[#FF3B30] text-white text-[8px] font-bold uppercase px-1 py-0.5">Main</span>}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        {i !== 0 && (
                          <button type="button" title="Set as main photo" data-testid={`set-main-${i}`}
                            onClick={() => setForm((f) => { const a = [...f.images]; const [m] = a.splice(i, 1); a.unshift(m); return { ...f, images: a }; })}
                            className="text-white hover:text-[#EAB308]"><Star size={16} /></button>
                        )}
                        <button type="button" title="Remove photo" data-testid={`remove-image-${i}`}
                          onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }))}
                          className="text-white hover:text-[#FF3B30]"><Trash2 size={16} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-[11px] text-zinc-600">No photos yet. Upload from your computer, or paste an image URL below. The first photo is the main one shown on the store.</p>}
              <div className="flex gap-2">
                <input className={field} placeholder="…or paste an image URL" value={imgUrl}
                  onChange={(e) => setImgUrl(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (imgUrl.trim()) { setForm((f) => ({ ...f, images: [...f.images, imgUrl.trim()] })); setImgUrl(""); } } }}
                  data-testid="form-image-url" />
                <button type="button" data-testid="add-image-url-btn" onClick={() => { if (imgUrl.trim()) { setForm((f) => ({ ...f, images: [...f.images, imgUrl.trim()] })); setImgUrl(""); } }} className="border border-zinc-700 text-xs font-bold uppercase tracking-widest px-4 hover:border-white transition-colors">Add</button>
              </div>
            </div>
            <input className={field} placeholder="Sizes (comma separated, e.g. S, M, L)" value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} data-testid="form-sizes" />
            <input className={field} placeholder="Colors (comma separated, e.g. Black, White, Red)" value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} data-testid="form-colors" />
            {form.sizes.split(",").map((s) => s.trim()).filter(Boolean).length > 0 && (
              <div className="border border-zinc-800 p-3 space-y-2" data-testid="size-price-editor">
                <div className="text-xs font-bold uppercase tracking-widest text-zinc-400">Per-Size Price <span className="text-zinc-600 normal-case font-normal">(optional — leave blank to use base price)</span></div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {form.sizes.split(",").map((s) => s.trim()).filter(Boolean).map((s) => (
                    <div key={s} className="flex items-center gap-2">
                      <span className="text-xs font-bold text-zinc-300 w-16 truncate" title={s}>{s}</span>
                      <div className="relative flex-1">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-zinc-500">$</span>
                        <input type="number" step="0.01" min="0" data-testid={`size-price-${s}`}
                          className={field + " pl-5"} placeholder={form.price || "0.00"}
                          value={form.size_prices?.[s] ?? ""}
                          onChange={(e) => setForm((f) => ({ ...f, size_prices: { ...f.size_prices, [s]: e.target.value } }))} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 items-center">
              <input className={field} type="number" placeholder="Stock" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="form-stock" />
              <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="form-featured" /> Featured</label>
            </div>

            {/* FULFILLMENT */}
            <div className="border border-zinc-800 p-4 space-y-3 mt-2">
              <div className="text-xs font-bold uppercase tracking-widest text-zinc-400">Fulfillment Method</div>
              <div className="grid grid-cols-2 gap-2" data-testid="fulfiller-toggle">
                <button type="button" data-testid="fulfiller-printful-btn"
                  onClick={() => setForm((f) => ({ ...f, fulfiller: "printful" }))}
                  className={`flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-widest py-3 border transition-colors ${form.fulfiller !== "shopify_ninjapod" ? "bg-sky-500 text-black border-sky-500" : "border-zinc-700 text-zinc-400 hover:border-white"}`}>
                  <Printer size={15} /> Fulfill via Printful
                </button>
                <button type="button" data-testid="fulfiller-shopify-btn"
                  onClick={() => setForm((f) => ({ ...f, fulfiller: "shopify_ninjapod" }))}
                  className={`flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-widest py-3 border transition-colors ${form.fulfiller === "shopify_ninjapod" ? "bg-[#22C55E] text-black border-[#22C55E]" : "border-zinc-700 text-zinc-400 hover:border-white"}`}>
                  <Store size={15} /> Fulfill via Shopify (NinjaPOD)
                </button>
              </div>

              {form.fulfiller === "shopify_ninjapod" ? (
                <div className="border border-green-500/40 bg-zinc-950 p-4 space-y-2" data-testid="ninjapod-fulfillment-panel">
                  <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-[#22C55E]"><Store size={16} /> Fulfilled by NinjaPOD (via Shopify)</div>
                  {Object.keys(form.shopify_variants || {}).length > 0 ? (
                    <>
                      <p className="text-[11px] text-zinc-400 leading-relaxed">Orders for this product route to NinjaPOD automatically, no Printful setup needed. <span className="text-zinc-300 font-bold">{Object.keys(form.shopify_variants || {}).length}</span> variant(s) mapped.</p>
                      <p className="text-[11px] text-zinc-500">To change the design, price or blank, edit it in the NinjaPOD app, then re-import it from the Shopify tab.</p>
                    </>
                  ) : (
                    <p className="text-[11px] text-[#EAB308] leading-relaxed">⚠ No Shopify variants mapped yet — orders can't route to NinjaPOD. Open the <span className="font-bold">Shopify tab</span> and re-import this product to link its variants.</p>
                  )}
                </div>
              ) : (
              <>
              <label className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-[#EAB308]">
                <input type="checkbox" checked={form.pod_enabled} onChange={(e) => setForm({ ...form, pod_enabled: e.target.checked })} data-testid="form-pod-enabled" />
                <Printer size={16} /> Auto-forward paid orders to Printful
              </label>
              {form.pod_enabled && (
                <>
                  <div className="flex gap-2 items-start">
                    <input className={field} placeholder="Design file URL (public PNG, ~300 DPI)" value={form.print_file_url} onChange={(e) => setForm({ ...form, print_file_url: e.target.value })} data-testid="form-print-file" />
                    <ImageUpload testId="pod-file-upload" label="Upload" onUploaded={(url) => setForm((f) => ({ ...f, print_file_url: url }))} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <select className={field} value={form.pod_placement} onChange={(e) => setForm({ ...form, pod_placement: e.target.value })} data-testid="form-placement">
                      {["front", "back", "left", "right"].map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <input className={field} placeholder="Variant map, e.g. S:4011, M:4012, L:4013  (or default:4012)" value={form.variantsRaw} onChange={(e) => setForm({ ...form, variantsRaw: e.target.value })} data-testid="form-variants" />
                  <button type="button" onClick={openPicker} data-testid="catalog-picker-toggle" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#EAB308] hover:text-white transition-colors">
                    <Search size={14} /> {pfOpen ? "Hide" : "Find / Import from"} Printful
                  </button>

                  {pfOpen && (
                    <div className="border border-zinc-700 bg-zinc-950 p-3 space-y-3" data-testid="catalog-picker">
                      <div className="flex gap-1 border border-zinc-800 p-1">
                        <button type="button" onClick={() => { setPfMode("store"); if (pfStore.length === 0) loadStoreProducts(); }} data-testid="picker-mode-store" className={`flex-1 text-[11px] font-bold uppercase tracking-widest py-2 transition-colors ${pfMode === "store" ? "bg-[#EAB308] text-black" : "text-zinc-400 hover:text-white"}`}>My Printful Products</button>
                        <button type="button" onClick={() => setPfMode("catalog")} data-testid="picker-mode-catalog" className={`flex-1 text-[11px] font-bold uppercase tracking-widest py-2 transition-colors ${pfMode === "catalog" ? "bg-[#EAB308] text-black" : "text-zinc-400 hover:text-white"}`}>Catalog Blanks</button>
                      </div>

                      {pfMode === "store" && (
                        <div className="space-y-2" data-testid="store-products-list">
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] uppercase tracking-widest text-zinc-500">Products you designed in Printful</span>
                            <button type="button" onClick={loadStoreProducts} disabled={pfBusy} className="text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white">{pfBusy ? "…" : "Refresh"}</button>
                          </div>
                          {pfStore.length > 0 ? (
                            <div className="max-h-56 overflow-y-auto divide-y divide-zinc-800 border border-zinc-800">
                              {pfStore.map((p) => (
                                <button type="button" key={p.id} onClick={() => importStoreProduct(p.id)} data-testid={`store-product-${p.id}`} className="w-full flex items-center gap-3 p-2 text-left hover:bg-zinc-900 transition-colors">
                                  {p.thumbnail && <img src={p.thumbnail} alt="" className="h-10 w-10 object-cover border border-zinc-800" />}
                                  <span className="text-xs text-zinc-300 flex-1">{p.name}</span>
                                  <span className="text-[10px] uppercase tracking-widest text-[#EAB308]">Import →</span>
                                </button>
                              ))}
                            </div>
                          ) : <p className="text-[11px] text-zinc-600 py-3 text-center">{pfBusy ? "Loading…" : "No products in your Printful store yet."}</p>}
                        </div>
                      )}

                      {pfMode === "catalog" && (<>
                      <div className="flex gap-2">
                        <input className={field} placeholder="Search Printful blanks (e.g. 3001 tee, hoodie, sticker)" value={pfSearch}
                          onChange={(e) => setPfSearch(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchCatalog(); } }}
                          data-testid="catalog-search-input" />
                        <button type="button" onClick={searchCatalog} disabled={pfBusy} data-testid="catalog-search-btn" className="border border-zinc-700 text-xs font-bold uppercase tracking-widest px-4 hover:border-white transition-colors disabled:opacity-50">{pfBusy ? "…" : "Search"}</button>
                      </div>

                      {!pfSelected && pfResults.length > 0 && (
                        <div className="max-h-52 overflow-y-auto divide-y divide-zinc-800 border border-zinc-800">
                          {pfResults.map((p) => (
                            <button type="button" key={p.id} onClick={() => selectCatalogProduct(p.id)} data-testid={`catalog-result-${p.id}`} className="w-full flex items-center gap-3 p-2 text-left hover:bg-zinc-900 transition-colors">
                              {p.image && <img src={p.image} alt="" className="h-10 w-10 object-cover border border-zinc-800" />}
                              <span className="text-xs text-zinc-300">{p.title}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      {pfSelected && (
                        <div className="space-y-3" data-testid="catalog-selected">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-white">{pfSelected.title}</span>
                            <button type="button" onClick={() => setPfSelected(null)} className="text-[11px] uppercase tracking-widest text-zinc-500 hover:text-white">← Back to results</button>
                          </div>
                          {pfSelected.colors?.length > 0 && (
                            <div>
                              <label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-1">Color</label>
                              <select className={field} value={pfColor} onChange={(e) => setPfColor(e.target.value)} data-testid="catalog-color-select">
                                {pfSelected.colors.map((cl) => <option key={cl} value={cl}>{cl}</option>)}
                              </select>
                            </div>
                          )}
                          <div className="max-h-32 overflow-y-auto text-[11px] text-zinc-400 border border-zinc-800 p-2">
                            {pfSelected.variants.filter((v) => !pfColor || v.color === pfColor).map((v) => (
                              <div key={v.id} className="flex justify-between"><span>{v.size || "One size"}</span><span className="text-zinc-300">{v.id}</span></div>
                            ))}
                          </div>
                          <button type="button" onClick={applyCatalogVariants} data-testid="catalog-apply-btn" className="w-full bg-[#EAB308] text-black font-bold uppercase tracking-widest text-xs px-4 py-2.5 hover:bg-white transition-colors">Use These IDs</button>
                        </div>
                      )}
                      </>)}
                    </div>
                  )}

                  {editing && (
                    <div className="border border-zinc-800 bg-zinc-950 p-3 space-y-2" data-testid="profit-preview">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-widest text-[#EAB308] flex items-center gap-2"><TrendingUp size={14} /> Profit Preview</span>
                        <button type="button" onClick={() => fetchProfit(editing.id)} disabled={profitLoading} data-testid="calc-profit-button" className="text-[11px] font-bold uppercase tracking-widest border border-zinc-700 px-3 py-1.5 hover:border-white transition-colors disabled:opacity-50">{profitLoading ? "Calculating…" : "Calculate Margins"}</button>
                      </div>
                      {profit && !profit.available && <p className="text-[11px] text-zinc-500">{profit.reason}</p>}
                      {profit && profit.available && (
                        <div className="space-y-2">
                          <div className="overflow-x-auto">
                            <table className="w-full text-[11px]">
                              <thead><tr className="text-left text-zinc-500 uppercase tracking-widest">
                                <th className="py-1">Variant</th><th className="py-1 text-right">You Charge</th><th className="py-1 text-right">Cost</th><th className="py-1 text-right">Profit</th><th className="py-1 text-right">Margin</th>
                              </tr></thead>
                              <tbody>
                                {profit.rows.map((r) => (
                                  <tr key={r.variant} className="border-t border-zinc-800" data-testid={`profit-row-${r.variant}`}>
                                    <td className="py-1.5 text-zinc-300">{r.variant}</td>
                                    <td className="py-1.5 text-right text-white">${r.retail.toFixed(2)}</td>
                                    <td className="py-1.5 text-right text-zinc-400">{r.cost != null ? `$${r.cost.toFixed(2)}` : "—"}</td>
                                    <td className={`py-1.5 text-right font-bold ${r.profit != null && r.profit >= 0 ? "text-green-400" : "text-[#FF3B30]"}`}>{r.profit != null ? `$${r.profit.toFixed(2)}` : "—"}</td>
                                    <td className={`py-1.5 text-right ${r.margin != null && r.margin >= 0 ? "text-green-400" : "text-[#FF3B30]"}`}>{r.margin != null ? `${r.margin}%` : "—"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {profit.avg_margin != null && <p className="text-[11px] text-zinc-400">Avg margin: <span className="text-green-400 font-bold">{profit.avg_margin}%</span> · Costs are live from Printful (excl. shipping/tax).</p>}
                        </div>
                      )}
                    </div>
                  )}

                  <p className="text-[11px] text-zinc-500 leading-relaxed"><strong>Easiest:</strong> use <strong>Find / Import from Printful</strong> → <strong>My Printful Products</strong> to import a product you designed (pulls all sizes, mockup photos & price automatically). Or map catalog variant IDs manually as <code className="text-zinc-300">S:4011, M:4012</code> (or <code className="text-zinc-300">default:ID</code> for sizeless items).</p>
                </>
              )}
              </>
              )}
            </div>

            <button type="submit" data-testid="save-product-button" className="w-full bg-[#FF3B30] text-white font-bold uppercase tracking-widest text-sm px-8 py-3 hover:bg-white hover:text-black transition-colors duration-200">Save Product</button>
          </form>
        </div>
      )}
    </div>
  );
}
